import { Box, Typography, ToggleButton, ToggleButtonGroup, Divider } from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import LightModeIcon from "@mui/icons-material/LightMode";
import DarkModeIcon from "@mui/icons-material/DarkMode";

const PAGES = [
  { id: "go",       label: "GO Schedule" },
  { id: "transit",  label: "Transit Status" },
  { id: "dispatch", label: "Police & Fire" },
  { id: "weather",  label: "Weather & Pollen" },
  { id: "calendar", label: "Calendar" },
  { id: "raccoons", label: "Animal Sightings" },
  { id: "worldcup", label: "World Cup 2026" },
];

const DIVISIONS = [
  "All","D11","D12","D13","D14","D22","D23",
  "D31","D32","D33","D41","D42","D43","D51","D52","D53","D55",
];

export const SETTINGS_KEY = "md_user_settings";

export function loadSettings() {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (!raw) return {};
    return JSON.parse(raw);
  } catch { return {}; }
}

export function saveSettings(settings) {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  } catch {}
}

function Section({ label, children }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase",
        letterSpacing: "0.07em", color: "text.secondary", mb: 1.25 }}>
        {label}
      </Typography>
      {children}
    </Box>
  );
}

export default function SettingsPage({ dark, border, barBg, settings, onSettingsChange }) {

  function update(key, value) {
    const next = { ...settings, [key]: value };
    onSettingsChange(next);
    saveSettings(next);
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>

      {/* Header */}
      <Box sx={{ height: 52, display: "flex", alignItems: "center", px: 2, gap: 1,
        borderBottom: `1px solid ${border}`, bgcolor: barBg, flexShrink: 0 }}>
        <SettingsIcon sx={{ fontSize: 20, color: "text.secondary", flexShrink: 0 }} />
        <Typography sx={{ fontSize: "0.85rem", fontWeight: 700 }}>Settings</Typography>
      </Box>

      {/* Content */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", bgcolor: "background.default" }}>
        <Box sx={{ px: 2, py: 2.5, maxWidth: 480 }}>

          <Section label="Appearance">
            <ToggleButtonGroup
              value={settings.colorMode || "dark"}
              exclusive
              onChange={(_, v) => v && update("colorMode", v)}
              size="small" fullWidth>
              <ToggleButton value="light" sx={{ flex: 1, gap: 0.75 }}>
                <LightModeIcon sx={{ fontSize: 15 }} /> Light
              </ToggleButton>
              <ToggleButton value="dark" sx={{ flex: 1, gap: 0.75 }}>
                <DarkModeIcon sx={{ fontSize: 15 }} /> Dark
              </ToggleButton>
            </ToggleButtonGroup>
          </Section>

          <Divider sx={{ mb: 3 }} />

          <Section label="Default page">
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
              {PAGES.map(page => {
                const selected = (settings.defaultPage || "go") === page.id;
                return (
                  <Box key={page.id} onClick={() => update("defaultPage", page.id)}
                    sx={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                      px: 1.5, py: 1.25, borderRadius: 1.5, cursor: "pointer",
                      border: `1px solid ${selected ? "#3b82f6" : border}`,
                      bgcolor: selected ? (dark ? "#1e3a5f44" : "#eff6ff") : "background.paper",
                      transition: "all 0.15s",
                      "&:hover": { borderColor: "#3b82f6" },
                    }}>
                    <Typography sx={{ fontSize: "0.875rem", color: selected ? (dark ? "#93c5fd" : "#1d4ed8") : "text.primary", fontWeight: selected ? 600 : 400 }}>
                      {page.label}
                    </Typography>
                    {selected && (
                      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#3b82f6" }} />
                    )}
                  </Box>
                );
              })}
            </Box>
          </Section>

          <Divider sx={{ mb: 3 }} />

          <Section label="Default Police & Fire division">
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
              {DIVISIONS.map(div => {
                const selected = (settings.defaultDivision || "D55") === div;
                return (
                  <Box key={div} onClick={() => update("defaultDivision", div)}
                    sx={{ px: 1.5, py: 0.6, borderRadius: 1, cursor: "pointer",
                      border: `1px solid ${selected ? "#3b82f6" : border}`,
                      bgcolor: selected ? (dark ? "#1e3a5f44" : "#eff6ff") : "background.paper",
                      transition: "all 0.15s",
                      "&:hover": { borderColor: "#3b82f6" },
                    }}>
                    <Typography sx={{ fontSize: "0.8rem", fontWeight: selected ? 700 : 400,
                      color: selected ? (dark ? "#93c5fd" : "#1d4ed8") : "text.secondary" }}>
                      {div === "All" ? "All divisions" : div}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Section>

          <Divider sx={{ mb: 3 }} />

          <Typography sx={{ fontSize: "0.75rem", color: "text.secondary", lineHeight: 1.6 }}>
            Settings are saved to this device and applied automatically on your next visit.
          </Typography>

        </Box>
      </Box>

      {/* Footer */}
      <Box sx={{ height: 52, bgcolor: barBg, borderTop: `1px solid ${border}`, px: 2,
        display: "flex", alignItems: "center", flexShrink: 0 }}>
        <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.65rem" }}>
          MAIN // DANFORTH
        </Typography>
      </Box>
    </Box>
  );
}
